/* eslint-disable prettier/prettier */
import { MouseEventHandler, useMemo, useRef } from 'react';

import { Box, Grid, SpeedDial, SpeedDialAction, speedDialClasses, Stack } from '@mui/material';
import { Bell, ClockCounterClockwise, List, ShuffleAngular, X } from 'phosphor-react';
import { useClickAway, useEvent } from 'react-use';

import { useNotifications } from '~components/hooks/notifications/useNotifications';
import { useSession } from '~components/hooks/useSession';
import { useSidebarWidth } from '~components/hooks/useSidebarWidth';
import { black, primaryColors, secondaryColors } from '~components/theme/styles/colors';

import { ImageVestaLogoV2 } from '../images';

import { AccountDialAnnouncements } from './content/announcements';
import { AccountDialAnnouncementsBanner } from './content/announcementsBanner';
import { AccountDialBridges } from './content/bridges';
import { AccountDialConnection } from './content/connection';
import { AccountDialConnectionButtons } from './content/connectionButtons';
import { AccountDialCurrentNetwork } from './content/currentNetwork';
import { AccountDialNetworks } from './content/networkSwitch';
import { AccountDialOverview } from './content/overview';
import { AccountDialSettings } from './content/settings';
import { AccountDialTransactions } from './content/transactions';
import { useAccountDial } from './provider';
import { TransactionNotifications } from './transactionNotifications';
import { DialOptions } from './types';

export function useAccountDialActions(
  speedDialOnClick: ({
    dialOption,
    superView,
    setAllNotificationRead,
  }: {
    dialOption: DialOptions;
    superView?: boolean;
    setAllNotificationRead?: () => void;
  }) => MouseEventHandler<HTMLDivElement>,
) {
  const { dialContentState } = useAccountDial();
  const { notificationsRead, notificationBubbleSx, setAllNotificationRead } = useNotifications();

  const speedDialActions = [
    {
      icon: (
        <Bell size={22} color={dialContentState.Announcements ? primaryColors[100] : primaryColors[600]} />
      ),
      tooltipTitle: 'Announcements',
      onClick: speedDialOnClick({ dialOption: DialOptions.ANNOUNCEMENTS, setAllNotificationRead }),
    },
    {
      icon: (
        <ClockCounterClockwise
          size={22}
          color={dialContentState.Transactions ? primaryColors[100] : primaryColors[600]}
        />
      ),
      tooltipTitle: 'Transactions',
      onClick: speedDialOnClick({ dialOption: DialOptions.TRANSACTION }),
    },
    {
      icon: (
        <ShuffleAngular
          size={22}
          color={dialContentState.Bridges ? primaryColors[100] : primaryColors[600]}
        />
      ),
      tooltipTitle: 'Bridges',
      onClick: speedDialOnClick({ dialOption: DialOptions.BRIDGES }),
    },
    {
      icon: <List size={22} color={dialContentState.SuperView ? primaryColors[100] : primaryColors[600]} />,
      tooltipTitle: 'Display All Menu Items',
      onClick: speedDialOnClick({ dialOption: DialOptions.SUPER_VIEW, superView: true }),
    },
  ];

  return speedDialActions.map(({ icon, tooltipTitle, onClick }, key) => (
    <SpeedDialAction
      sx={{
        height: '48px',
        width: '48px',
        margin: '4px',
        background: black['45%'],
        ...(tooltipTitle === 'Announcements' && !notificationsRead ? notificationBubbleSx : {}),
      }}
      key={key}
      icon={icon}
      onClick={onClick}
      tooltipTitle={tooltipTitle}
    />
  ));
}

export const AccountDial = () => {
  const {
    dialOpen,
    setDialOpen,
    switchDialContent,
    resetAll,
    dialContentState,
    isFullscreen,
    isLifiWidgetOpen,
  } = useAccountDial();
  const { isConnected } = useSession();
  const speedDialOnClick =
    ({
      dialOption,
      superView = false,
      setAllNotificationRead,
    }: {
      dialOption: DialOptions;
      superView?: boolean;
      setAllNotificationRead?: () => void;
    }): MouseEventHandler =>
    e => {
      e.stopPropagation();
      switchDialContent(dialOption);
      setDialOpen(true);
      if (superView) {
        setDialOpen(false);
      }
      if (setAllNotificationRead) {
        setAllNotificationRead();
      }
    };

  const barRef = useRef(null);

  const closeDial = () => {
    if (!isLifiWidgetOpen) {
      setDialOpen(false);
      resetAll();
    }
  };

  useEvent('keyup', ({ key }: { key: string }) => {
    if (key === 'Escape') {
      closeDial();
    }
  });

  useClickAway(barRef, closeDial);

  const dialActionArray = useAccountDialActions(speedDialOnClick);

  const dialOptionContent = useMemo(
    () => [
      {
        state: dialContentState.Connect,
        content: (
          <AccountDialConnection
            forceOpen
            onConnect={() => {
              resetAll();
              setDialOpen(false);
            }}
          />
        ),
      },
      { state: dialContentState.Announcements, content: <AccountDialAnnouncements forceOpen /> },
      { state: dialContentState.Transactions, content: <AccountDialTransactions forceOpen /> },
      { state: dialContentState['Switch Network'], content: <AccountDialNetworks forceOpen /> },
      {
        state: dialContentState.Settings,
        content: (
          <AccountDialSettings
            forceOpen
            viewAllClick={speedDialOnClick({ dialOption: DialOptions.SUPER_VIEW, superView: true })}
          />
        ),
      },
      { state: dialContentState.Bridges, content: <AccountDialBridges forceOpen /> },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dialContentState],
  );

  const sidebarWidth = useSidebarWidth();

  return (
    <Box
      ref={barRef}
      sx={{
        position: 'absolute',
        height: '50%',
        width: isFullscreen ? '100%' : `calc(100% - ${sidebarWidth}px)`,
        zIndex: 1301,
        top: 0,
        left: sidebarWidth,
        pointerEvents: 'none',
      }}
    >
      {!dialContentState.SuperView ? (
        <Box
          position={'fixed'}
          sx={{
            background:
              'linear-gradient(0deg, rgba(2,0,36,0) 10%, rgba(0,0,0,0.7637429971988796) 52%, rgba(0,0,0,1) 80%)',
            opacity: dialOpen ? 0.85 : 0,
            transition: 'opacity 0.25s ease-in, width 0.15s linear, left 0.15s linear',
            pointerEvents: 'none',
            width: isFullscreen ? '100%' : `calc(100% - ${sidebarWidth}px)`,
            backdropFilter: 'blur(4px)',
          }}
          left={isFullscreen ? 0 : { xs: 0, sm: sidebarWidth }}
          height={'50%'}
          zIndex={1302}
        />
      ) : null}
      {dialOpen && (
        <Stack
          direction={'row'}
          position={'fixed'}
          top={48}
          right={128}
          width={`calc(100% - ${sidebarWidth}px - 128px - 64px)`}
          sx={{
            pointerEvents: dialOpen ? 'all' : 'none',
            opacity: dialOpen ? 1 : 0,
            transition: 'opacity 0.25s ease-in-out, width 0.15s linear',
            gap: '16px',
          }}
          zIndex={1302}
        >
          <AccountDialAnnouncementsBanner />
          <Box onClick={speedDialOnClick({ dialOption: DialOptions.SWITCH_NET })}>
            <AccountDialCurrentNetwork />
          </Box>
          <AccountDialConnectionButtons onDisconnect={() => setDialOpen(false)} />
        </Stack>
      )}
      <Grid sx={{ position: 'fixed', top: 40, right: 32, zIndex: 1304 }}>
        <SpeedDial
          ariaLabel="Account-Dial"
          aria-labelledby="Account-Dial"
          sx={{
            position: 'relative',
            marginBottom: '8px',
            [`.${speedDialClasses.fab}`]: { background: black['85%'], height: '64px', width: '64px' },
          }}
          icon={
            !dialContentState.SuperView ? (
              <ImageVestaLogoV2
                height={32}
                width={32}
                background={isConnected ? secondaryColors[200] : primaryColors[100]}
              />
            ) : (
              <X size={24} color={primaryColors[400]} />
            )
          }
          open={dialOpen}
          direction={'down'}
          onClick={() => {
            setDialOpen(oldState => {
              if (dialContentState.SuperView) {
                resetAll();
                return false;
              }
              if (oldState) resetAll();
              return !oldState;
            });
          }}
        >
          {isConnected && dialOpen && dialActionArray}
        </SpeedDial>
        {dialOptionContent.map((option, index) =>
          option.state ? (
            <Box
              zIndex={option.state ? 1302 : -1}
              key={index}
              position={'fixed'}
              top={112}
              right={100}
              width={350}
              sx={{
                opacity: option.state ? 1 : 0,
                display: option.state ? undefined : 'none',
                transition: 'opacity 0.25s ease-in-out',
                pointerEvents: option.state ? 'all' : 'none',
              }}
            >
              {option.content}
            </Box>
          ) : null,
        )}
        {dialContentState.SuperView ? <AccountDialOverview show={dialContentState.SuperView} /> : null}
        <TransactionNotifications
          viewAllTransactions={speedDialOnClick({ dialOption: DialOptions.TRANSACTION })}
        />
      </Grid>
    </Box>
  );
};
